if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.setUpViewerNav();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setUpViewerNav() {
        const prevButton = this.elements.viewer.querySelector(':scope > .slider-buttons .slider-button--prev');
        const nextButton = this.elements.viewer.querySelector(':scope > .slider-buttons .slider-button--next');
        if (!prevButton || !nextButton) return;

        // Capture the click before SliderComponent's generic scroll navigation.
        // Thumbnail desktop layouts use display:none for inactive slides, so their
        // zero-width offsets make the generic handler and gallery handler conflict.
        prevButton.addEventListener('click', this.onViewerNavClick.bind(this, -1), true);
        nextButton.addEventListener('click', this.onViewerNavClick.bind(this, 1), true);
      }

      onViewerNavClick(direction, event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.viewerNavPending) return;

        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('.slider__slide[data-media-id]'));
        if (mediaItems.length < 2) return;
        const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        const currentIndex = activeIndex === -1 ? 0 : activeIndex;
        const nextIndex = (currentIndex + direction + mediaItems.length) % mediaItems.length;
        const nextMediaId = mediaItems[nextIndex].dataset.mediaId;

        // Apply the final state after all synchronous click work has completed. This
        // prevents another slider listener from leaving the viewer with no active LI.
        this.viewerNavPending = true;
        window.requestAnimationFrame(() => {
          this.setActiveMedia(nextMediaId, false);
          this.viewerNavPending = false;
        });
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`.slider__slide[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.slider__slide[data-media-id]');
        if (!activeMedia) {
          return;
        }
        // Thumbnail layouts hide inactive media with display:none on desktop. Hidden
        // slides can therefore keep the reveal-on-scroll offscreen class forever,
        // leaving the newly active image at opacity: 0.01. Gallery navigation is an
        // explicit reveal, so cancel that page-scroll animation before showing it.
        // Activate the target before deactivating its siblings so the gallery never
        // passes through a state where every media item is hidden.
        activeMedia.classList.add('scroll-trigger--cancel');
        activeMedia.classList.remove('scroll-trigger--offscreen');
        activeMedia.classList.add('is-active');
        this.elements.viewer.querySelectorAll('.slider__slide[data-media-id]').forEach((element) => {
          if (element !== activeMedia) element.classList.remove('is-active');
        });

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (!activeThumbnail) return;
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
